import * as React from 'react';

import { IDeviceTableProps } from './commission-interface';
import { COMMISSION_STATUS, commissionStatusTagComponent } from '../commissioning-upload';
import { IDeviceInfo, SCR_TAMPER_STATUS } from 'dto/device-dto';
import { EXPORT_TYPES, exportData, isAllowedDeviceType, isNullOrEmpty } from 'common-utils';
import { DEFAULT_DEVICE_COLUMN, DevicesTableComponent } from 'pages/private/components/devices-table-component';
import { hasContainsIgnoreCase, localeCompare } from 'common-utils/string-utils';
import { Select, SelectFormItem, SelectFormItemWithSkeleton } from 'ui-lib';
import { observer } from 'mobx-react';

interface IDeviceRecord extends IDeviceInfo {
    commissionStatus: string;
}
export const SelectDevicesWrapper = observer((props: IDeviceTableProps) => {
    const { data, onChange, setSelectedPlatform, selectedPlatform, platforms } = props;

    const devices = React.useMemo(() => {
        if (isNullOrEmpty(data.dataSource)) return [];
        return data.dataSource.map<IDeviceRecord>((device) => ({
            UID: device.uid,
            type: device.deviceTypeName,
            esn: device.esn,
            serialNumber: device.serial,
            commissionStatus: device.status,
        }));
    }, [data.dataSource, selectedPlatform]);

    const selectedDevices = React.useMemo(() => {
        return data.defaultSelectedRows?.selectedValues;
    }, [data.defaultSelectedRows]);

    const title = `Provisioned Devices for ${data.name}`;

    return (
        <React.Fragment>
            <DevicesTableComponent
                dataSource={devices}
                title={title}
                loadingState={data.loading}
                hiddenDefaultColumns={[
                    DEFAULT_DEVICE_COLUMN.STATUS,
                    DEFAULT_DEVICE_COLUMN.DEVICE_GROUP,
                    DEFAULT_DEVICE_COLUMN.LAST_UPDATE,
                ]}
                extraColumns={[
                    {
                        code: 'commissionStatus',
                        title: 'Status',
                        dataIndex: 'commissionStatus',
                        filters: [
                            { text: COMMISSION_STATUS.COMMISSIONABLE, value: COMMISSION_STATUS.COMMISSIONABLE },
                            { text: SCR_TAMPER_STATUS.SCR_TAMPER, value: SCR_TAMPER_STATUS.SCR_TAMPER },
                            { text: SCR_TAMPER_STATUS.SCR_BATTERY_NOT_OK, value: SCR_TAMPER_STATUS.SCR_BATTERY_NOT_OK },
                            { text: SCR_TAMPER_STATUS.INVALID_FIRMWARE, value: SCR_TAMPER_STATUS.INVALID_FIRMWARE },
                        ],
                        sorter: (a, b) => localeCompare(a.commissionStatus, b.commissionStatus),
                        render: commissionStatusTagComponent,
                        onFilter: (status: COMMISSION_STATUS, device: IDeviceRecord) =>
                            hasContainsIgnoreCase(device.commissionStatus, status),
                    },
                ]}
                buildDeviceLinkProps={(record: IDeviceRecord) => ({
                    accountId: data.accountId,
                })}
                extraItems={[
                    <Select
                        loading={data.loading}
                        key='platform'
                        dataSource={platforms?.map((item) => ({ value: item.code, label: item.name })) ?? []}
                        code='platform'
                        value={selectedPlatform?.code}
                        onChange={(value) => setSelectedPlatform(value)}
                    />,
                ]}
                exportOptions={{
                    hideExportItem: false,
                    overrideExportItem: {
                        filename: title,
                        componentProps: {
                            label: 'Download CSV',
                            type: 'primary',
                            loading: data.loading,
                            onClick:
                                !isNullOrEmpty(selectedDevices) &&
                                (() => exportData(title, EXPORT_TYPES.CSV, selectedDevices)),
                        },
                    },
                }}
                rowSelection={{
                    selectedRows: data.defaultSelectedRows,
                    getCheckboxProps: (record: IDeviceRecord) => {
                        return {
                            name: record['UID'],
                            disabled: record.commissionStatus != COMMISSION_STATUS.COMMISSIONABLE
                            || (!isNullOrEmpty(selectedPlatform) && !isAllowedDeviceType(selectedPlatform.deviceTypes, record.type)),
                        };
                    },
                    onChange: onChange,
                }}
            />
        </React.Fragment>
    );
});
